import { Alpine } from '../../core/alpine-core.js';
import { entityDocId, unwrapApiEntity } from '../../core/api-entity.js';
import { apiFetch } from '../api.js';

Alpine.data('playersList', () => ({

    // Data
    items: [],
    season: null,

    // UI state
    modals: { transfer: false, linkPerson: false },
    linkForm: {
        query: '',
        results: [],
        selected: null,
        targetPlayerId: '',
        claimCode: '',
        busy: false,
        error: '',
    },
    loading: true,
    refreshSpinning: false,

    placeholderLogo: '/assets/placeholder-player.png',

    // Filters / Pagination
    q: '',
    filters: { q: '', status: '', league: '', season: '', sortBy: 'fullName:asc', limit: 20 },
    statusFilter: '',          // '', 'active', 'inactive', 'pool' , 'reserve'
    page: 1,
    totalPages: 1,
    total: 0,

    filtersOpen: false,
    firstLoad: true,

    scrollToTopAfterPageChange() {
        if (window.innerWidth >= 768) return;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    // ===== Init =====
    async init() {
        // this.season = s;
        await this.reload();
        this.firstLoad = false;
    },

    // ===== Helpers =====
    _buildQuery() {
        const p = new URLSearchParams();
        if (this.filters.q) p.set('q', this.filters.q);
        if (this.filters.status) p.set('status', this.filters.status); // backend expects 'status'
        if (this.filters.sortBy) p.set('sortBy', this.filters.sortBy);
        p.set('page', String(this.page));
        p.set('limit', String(this.filters.limit));
        return p.toString();
    },

    async reload() {
        this.loading = true;
        try {
            const url = `/players?${this._buildQuery()}`;
            const res = await apiFetch(url);
            if (!res.ok) {
                this.$store.toastr.error('Failed to fetch players');
                return;
            }
            const data = await res.json();
            this.items = data.items || [];
            this.page = data.page || 1;
            this.totalPages = data.totalPages || 1;
            this.total = data.total || 0;
        } finally {
            this.loading = false;
            this.refreshSpinning = false;
        }
    },

    async manualReload() {
        this.refreshSpinning = true;
        this.page = 1;
        await this.reload();
        this.scrollToTopAfterPageChange();
    },

    hasActiveFilters() {
        // mark when any filter beyond defaults is active (excluding q empty & default sort/limit)
        const f = this.filters || {};
        return !!((f.q && f.q.trim()) ||
            (f.status && f.status !== '') ||
            (f.sortBy && f.sortBy !== 'fullName:asc') ||
            (Number(f.limit) !== 20));
    },

    async go(p) {
        const next = Math.min(Math.max(1, p), this.totalPages || 1);
        if (next === this.page) return;
        this.page = next;
        await this.reload();
        this.scrollToTopAfterPageChange();
    },

    // Keep these with your other state/methods
    showPager() {
        // hide pager when 0 or 1 page
        return Number(this.totalPages || 0) > 1;
    },
    from() {
        if (!this.total) return 0;
        const limit = Number(this.limit ?? this.filters?.limit ?? 20);
        return (this.page - 1) * limit + 1;
    },
    to() {
        if (!this.total) return 0;
        const limit = Number(this.limit ?? this.filters?.limit ?? 20);
        const end = this.page * limit;
        return end > this.total ? this.total : end;
    },

    getPlayerStatusMeta(status) {
        const map = {
            active: { label: 'Rostered', classes: 'border border-emerald-400/35 bg-emerald-500/15 text-emerald-200' },
            reserve: { label: 'Reserve', classes: 'border border-violet-400/35 bg-violet-500/12 text-violet-200' },
            pool: { label: 'Spare pool', classes: 'border border-sky-400/35 bg-sky-500/12 text-sky-200' },
            inactive: { label: 'Inactive', classes: 'border border-white/12 bg-white/5 text-neutral-400' },
        };
        return map[status] ?? map.inactive;
    },

    teamLabel(p) {
        return p?.teamName || p?.team?.name || '';
    },

    venueLabel(p) {
        return p?.venueName || p?.venue?.name || '';
    },

    teamCell(p) {
        const team = this.teamLabel(p);
        if (team) return team;
        if (p?.status === 'active' || p?.status === 'reserve') return 'Unassigned';
        return '—';
    },

    venueCell(p) {
        const venue = this.venueLabel(p);
        if (venue) return venue;
        if (p?.status === 'active' || p?.status === 'reserve') return '—';
        return '—';
    },

    formatMemberSince(value) {
        if (!value) return '—';
        const dt = new Date(value);
        if (Number.isNaN(dt.getTime())) return '—';
        return dt.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            timeZone: 'Asia/Bangkok',
        });
    },

    // ===== Transfer =====
    openTransfer(p) {
        this.transferForm = { _id: p._id, newTeamId: '' };
        this.modals.transfer = true;
    },

    async confirmTransfer() {
        const r = await apiFetch(`/players/${this.transferForm._id}/transfer`, {
            method: 'POST',
            body: { toTeamId: this.transferForm.newTeamId, type: 'roster' } // or loan; server reads toTeamId / teamId
        });
        const data = await r.json();
        if (!r.ok) return alert(data.error || 'Transfer failed');

        const row = unwrapApiEntity(data, ['player', 'item']);
        const rid = entityDocId(row);
        const i = rid ? this.items.findIndex(x => x._id === rid) : -1;
        if (i > -1 && row) {
            this.items[i] = {
                ...this.items[i],
                registeredTeamId: row.registeredTeamId,
                teamName: this.teams.find(t => t._id === row.registeredTeamId)?.name
            };
        }
        this.modals.transfer = false;
    },

    /** Manager+ only, and only when roster row is not yet tied to a global person. */
    canLinkPerson(p) {
        if (!this.$store.session?.canLinkRosterPlayers?.()) return false;
        return !String(p?.personId || '').trim();
    },

    openLinkPerson(p) {
        this.linkForm = {
            query: '',
            results: [],
            selected: null,
            targetPlayerId: p?._id || '',
            claimCode: '',
            busy: false,
            error: '',
        };
        this.modals.linkPerson = true;
    },

    async searchGlobalPeople() {
        const q = String(this.linkForm.query || '').trim();
        if (q.length < 2) {
            this.linkForm.results = [];
            return;
        }
        this.linkForm.busy = true;
        this.linkForm.error = '';
        try {
            const res = await apiFetch(`/people/search?q=${encodeURIComponent(q)}&limit=20`);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'Search failed');
            this.linkForm.results = Array.isArray(data.items) ? data.items : [];
        } catch (e) {
            this.linkForm.error = e.message || 'Search failed';
            this.linkForm.results = [];
        } finally {
            this.linkForm.busy = false;
        }
    },

    selectLinkPerson(person) {
        this.linkForm.selected = person || null;
    },

    async sendPersonInvite() {
        if (!this.linkForm.selected?.personId) {
            this.linkForm.error = 'Select a person from search results.';
            return;
        }
        this.linkForm.busy = true;
        this.linkForm.error = '';
        try {
            const body = { personId: this.linkForm.selected.personId };
            if (this.linkForm.targetPlayerId) body.targetPlayerId = this.linkForm.targetPlayerId;
            const res = await apiFetch('/players/invite-link', { method: 'POST', body });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'Invite failed');
            this.linkForm.claimCode = String(data.claimCode || data.claimToken || '');
        } catch (e) {
            this.linkForm.error = e.message || 'Invite failed';
        } finally {
            this.linkForm.busy = false;
        }
    },

    copyClaimCode() {
        const code = String(this.linkForm.claimCode || '').trim();
        if (!code) return;
        navigator.clipboard?.writeText(code).catch(() => {});
    }

}));
